// components/DegreeAudit/Progress/CreditProgressBar.tsx

import { memo } from 'react';
import {
  calculateMajorCredits,
  calculateProgressBarSegments,
} from '../../../utils/creditCalculations';
import { useCreditUnit } from '../../../contexts/InstitutionConfigContext';

export interface CreditProgressBarMajorData {
  credits_completed?: number;
  credits_in_progress?: number;
  credits_planned?: number;
  credits_suggested?: number;
  credits_available?: number;
}

export interface CreditProgressBarProps {
  majorData: CreditProgressBarMajorData | null | undefined;
  totalCredits?: number;
}

const CreditProgressBar = memo(
  ({ majorData, totalCredits = 120 }: CreditProgressBarProps) => {
    const creditUnit = useCreditUnit();
    const credits = calculateMajorCredits(majorData, totalCredits);
    const segments = calculateProgressBarSegments(credits, totalCredits);

    return (
      <div className="w-full">
        <div className="flex h-3 bg-surface-sunken rounded-full overflow-hidden">
          {segments.completed > 0 && (
            <div
              className="bg-green-500 transition-all duration-300"
              style={{ width: `${segments.completed}%` }}
              title={`${creditUnit.format(credits.credits_completed)} completed`}
            />
          )}

          {segments.in_progress > 0 && (
            <div
              className="bg-blue-500 transition-all duration-300"
              style={{ width: `${segments.in_progress}%` }}
              title={`${creditUnit.format(credits.credits_in_progress)} in progress`}
            />
          )}

          {segments.planned > 0 && (
            <div
              className="bg-yellow-500 transition-all duration-300"
              style={{ width: `${segments.planned}%` }}
              title={`${creditUnit.format(credits.credits_planned)} planned`}
            />
          )}

          {segments.suggested > 0 && (
            <div
              className="bg-status-suggested/50 transition-all duration-300"
              style={{ width: `${segments.suggested}%` }}
              title={`${creditUnit.format(credits.credits_suggested)} suggested`}
            />
          )}

          {segments.available > 0 && (
            <div
              className="bg-line-soft transition-all duration-300"
              style={{ width: `${segments.available}%` }}
              title={`${creditUnit.format(credits.credits_available)} available`}
            />
          )}
        </div>

        <div className="flex items-center justify-between mt-2 text-xs text-gray-600">
          <div className="flex items-center space-x-4 flex-wrap gap-y-1">
            <div className="flex items-center space-x-1">
              <div className="w-2 h-2 bg-green-500 rounded-full"></div>
              <span>Completed ({credits.credits_completed})</span>
            </div>
            <div className="flex items-center space-x-1">
              <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
              <span>In Progress ({credits.credits_in_progress})</span>
            </div>
            <div className="flex items-center space-x-1">
              <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>
              <span>Planned ({credits.credits_planned})</span>
            </div>

            {credits.credits_suggested > 0 && (
              <div className="flex items-center space-x-1">
                <div className="w-2 h-2 bg-status-suggested/50 rounded-full"></div>
                <span>Suggested ({credits.credits_suggested})</span>
              </div>
            )}

            {!credits.is_complete && credits.credits_available > 0 && (
              <div className="flex items-center space-x-1">
                <div className="w-2 h-2 bg-line-soft rounded-full"></div>
                <span>Available ({credits.credits_available})</span>
              </div>
            )}

            {credits.is_complete && (
              <div className="flex items-center space-x-1">
                <div className="w-2 h-2 bg-green-600 rounded-full"></div>
                <span className="text-green-700 font-medium">
                  Complete {credits.excess_credits > 0 && `(+${credits.excess_credits} excess)`}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }
);

CreditProgressBar.displayName = 'CreditProgressBar';

export default CreditProgressBar;
